﻿import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { modal } from "../utils/antdStatic";
import { logoutThunk } from "../redux/state/loginstate/user";
import { persistor } from "../redux/store";
import { STORAGE_SESSION_ACTIVE } from "../utils/storageKeys";
import DeleteSessions from "./DeleteSessions";

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 min de inactividad
const WARNING_BEFORE     =  5 * 60 * 1000; // avisar 5 min antes del cierre
const CHECK_INTERVAL     = 30 * 1000;       // verificar cada 30 s

const TimeOutSession = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { HandleDeleteSession } = DeleteSessions();

  const deleteRef      = useRef(HandleDeleteSession);
  const alertActiveRef = useRef(false);

  // Mantiene la referencia actualizada sin re-registrar efectos
  useEffect(() => {
    deleteRef.current = HandleDeleteSession;
  });

  // Cierra la sesión por inactividad mostrando el mensaje adecuado
  const handleSessionExpired = () => {
    if (alertActiveRef.current) return;
    alertActiveRef.current = true;

    sessionStorage.removeItem("expiretime");

    modal.warning({
      title: "Sesión expirada",
      content: "Su sesión ha expirado por inactividad. Por favor, inicie sesión nuevamente.",
      okText: "Aceptar",
      keyboard: false,
      maskClosable: false,
      onOk: async () => {
        await dispatch(logoutThunk());
        await persistor.purge();
        localStorage.removeItem(STORAGE_SESSION_ACTIVE);
        navigate("/", { replace: true });
      },
    });
  };

  // Intervalo de verificación de inactividad
  useEffect(() => {
    const check = async () => {
      const expireTime = parseInt(sessionStorage.getItem("expiretime") ?? "0", 10);
      if (!expireTime) return;

      const timeLeft = expireTime - Date.now();

      // Sesión vencida
      if (timeLeft <= 0) {
        await handleSessionExpired();
        return;
      }

      // Advertencia previa: quedan menos de WARNING_BEFORE ms
      if (timeLeft <= WARNING_BEFORE && !alertActiveRef.current) {
        alertActiveRef.current = true;
        const minutesLeft = Math.ceil(timeLeft / 60000);
        const label = minutesLeft === 1 ? "minuto" : "minutos";

        // Auto-cierra el modal y cierra sesión si el tiempo se agota antes de responder
        let modalRef = null;
        const autoCloseTimer = setTimeout(async () => {
          modalRef?.destroy();
          await deleteRef.current();
        }, timeLeft);

        modalRef = modal.confirm({
          title: "¿Desea continuar?",
          content: `Su sesión expirará en ${minutesLeft} ${label} por inactividad.`,
          okText: "Continuar sesión",
          cancelText: "Cerrar sesión",
          keyboard: false,
          maskClosable: false,
          onOk: () => {
            clearTimeout(autoCloseTimer);
            sessionStorage.setItem("expiretime", String(Date.now() + INACTIVITY_TIMEOUT));
            alertActiveRef.current = false;
          },
          onCancel: async () => {
            clearTimeout(autoCloseTimer);
            await deleteRef.current();
          },
        });
      }
    };

    const interval = setInterval(check, CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Escucha de actividad del usuario
  useEffect(() => {
    const update = () => {
      sessionStorage.setItem("expiretime", String(Date.now() + INACTIVITY_TIMEOUT));
      alertActiveRef.current = false;
    };

    update(); // establece el tiempo inicial al montar

    window.addEventListener("click",     update);
    window.addEventListener("keypress",  update);
    window.addEventListener("scroll",    update);
    window.addEventListener("mousemove", update);

    return () => {
      window.removeEventListener("click",     update);
      window.removeEventListener("keypress",  update);
      window.removeEventListener("scroll",    update);
      window.removeEventListener("mousemove", update);
    };
  }, []);

  return null;
};

export default TimeOutSession;
